import React from 'react';
import { Landmark, CreditCard, FileCheck, FileText, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ModernModuleDashboard from '../../shared/components/dashboard/ModernModuleDashboard';
import {
  buildDashboardCharts,
  pipelineFromKpis,
  withKpiVariants,
} from '../../shared/components/dashboard/moduleDashboardCharts';
import { formatBrandMoney } from '../../shared/utils/brandCurrency';

export default function BankingDashboardPage() {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.banque.pages.dashboardPage.${key}`, opts);

  return (
    <ModernModuleDashboard
      apiEndpoint="/banking/dashboard"
      title={p('title', { defaultValue: 'لوحة تحكم البنك' })}
      subtitle={p('subtitle', { defaultValue: 'مؤشرات الحسابات والتسويات' })}
      icon={Landmark}
      accent="from-indigo-600 to-blue-900"
      recentTitle={p('recent_transactions', { defaultValue: 'آخر العمليات' })}
      quickLinks={[
        { label: p('link_accounts', { defaultValue: 'الحسابات' }), href: '/banking/accounts', icon: CreditCard },
        { label: p('link_reconciliation', { defaultValue: 'التسوية' }), href: '/banking/reconciliation/general', icon: FileCheck },
        { label: p('link_checks', { defaultValue: 'الشيكات' }), href: '/banking/checks', icon: FileText },
        { label: p('link_analytics', { defaultValue: 'التحليلات' }), href: '/banking/analyses-rapports', icon: Landmark },
      ]}
      mapData={(data) => {
        const kpis = data.kpis || {};
        const chartRows = data.chartRows || [];

        return {
          hero: {
            label: p('kpi_total_balance', { defaultValue: 'إجمالي الأرصدة' }),
            value: formatBrandMoney(kpis.totalBalance || 0),
            icon: Landmark,
          },
          kpis: withKpiVariants([
            { label: p('kpi_accounts', { defaultValue: 'الحسابات' }), value: kpis.accountsCount ?? 0, icon: CreditCard },
            { label: p('kpi_reconciliations', { defaultValue: 'تسويات معلقة' }), value: kpis.pendingReconciliations ?? 0, icon: FileCheck },
            { label: p('kpi_checks', { defaultValue: 'شيكات معلقة' }), value: kpis.checksOutstanding ?? 0, icon: FileText },
            {
              label: p('kpi_pending_items', { defaultValue: 'بنود معلقة' }),
              value: (kpis.pendingReconciliations ?? 0) + (kpis.checksOutstanding ?? 0),
              icon: AlertTriangle,
            },
          ]),
          charts: buildDashboardCharts({
            pipeline: {
              title: p('chart_pipeline_title', { defaultValue: 'ملخص البنك' }),
              subtitle: p('chart_pipeline_subtitle', { defaultValue: 'حسابات وتسويات وشيكات' }),
              alwaysShow: true,
              rows: pipelineFromKpis([
                { label: p('kpi_accounts', { defaultValue: 'الحسابات' }), value: kpis.accountsCount ?? 0 },
                { label: p('kpi_reconciliations', { defaultValue: 'تسويات معلقة' }), value: kpis.pendingReconciliations ?? 0 },
                { label: p('kpi_checks', { defaultValue: 'شيكات معلقة' }), value: kpis.checksOutstanding ?? 0 },
              ]),
            },
            status: {
              title: p('chart_status_title', { defaultValue: 'حالة التسوية' }),
              subtitle: p('chart_status_subtitle', { defaultValue: 'مكتملة مقابل معلقة' }),
              active: Math.max(0, (kpis.accountsCount ?? 0) - (kpis.pendingReconciliations ?? 0)),
              inactive: kpis.pendingReconciliations ?? 0,
              alwaysShow: true,
            },
            ranked: {
              title: p('chart_accounts', { defaultValue: 'أرصدة الحسابات' }),
              subtitle: p('chart_accounts_subtitle', { defaultValue: 'حسب الرصيد' }),
              rows: chartRows,
            },
          }),
          recentItems: data.recentItems || [],
        };
      }}
    />
  );
}
